import { computed, ref, watch } from 'vue';
import { activityDetailStore, xAxisKeyMap } from '@client/store/activityDetailStore';

import Definition from '@client/config/definition';
import unitHelper from '@client/helper/unit';
import { dealAttrDefine, NULL_TEXT } from './utils/common';
import { initStore } from '@client/store/initStore';
import { newListData } from '../table/hooks/useLapData';

export const xAxisKey = xAxisKeyMap['distance-polyline'];

const bgSetting = {
  'theme-dark': 'rgba(24, 30, 48, 0.8)',
  'theme-light': 'rgba(255, 255, 255, 0.8)'
};

const clientWidth = document.body.clientWidth;
let watched = false;

export default function useTimeChart({ isOverview, activeAttributes, chartRef }) {
  const activityDetailStoreRef = activityDetailStore();
  const initStoreRef = initStore();
  const THEME = initStoreRef.theme;

  const activeAttributesTimeChart = ref([]);
  const chartData = computed(() => {
    if (isOverview) {
      return activityDetailStoreRef.polylineData;
    }

    return activityDetailStoreRef.frameSelection?.data;
  });
  const sportName2 = computed(() => activityDetailStoreRef.sportName);
  const summary2 = computed(() => activityDetailStoreRef.activityDetail?.summary);
  const selectedData = computed(() => activityDetailStoreRef.selectedData);
  const pauseList = computed(() => activityDetailStoreRef.pauseList);

  const graphObj = computed(() => activityDetailStoreRef.graphObj);

  // 选中的数据对应的遮罩区间
  const chartCoverZone = computed(() => {
    if (selectedData.value.length === 0) {
      return {
        lockedZone: [],
        openZone: []
      };
    }

    const startTimestamp = summary2.value.startTimestamp;
    const endTimestamp = summary2.value.endTimestamp;

    const SELECTED = 'selected';
    const UNSELECTED = 'unselected';
    const PASS = 'pass';

    // 合并时间区间

    // 每一行的选中状态
    const switchs = newListData.value.map((item, index) => {
      if (['Strength'].includes(sportName2.value) && Definition.isRestMode(item.mode)) {
        return PASS;
      }
      return selectedData.value.includes(index) ? SELECTED : UNSELECTED;
    });
    let lockedZone = [];
    let openZone = [];
    let open = null;
    switchs.forEach((status, index) => {
      if ([PASS].includes(status)) {
        return;
      }
      const item = {
        start: newListData.value[index].startTimestamp,
        end: newListData.value[index].endTimestamp + (newListData.value[index].transitionTime || 0)
      };
      if ([UNSELECTED].includes(status)) {
        if (open) {
          openZone.push(open);
          open = null;
        }
        return;
      }

      if (!open) {
        open = item;
        return;
      }
      open.end = item.end;
    });
    if (open) {
      openZone.push(open);
    }

    if (openZone.length === 0) {
      lockedZone = [];
      openZone = [];
    } else {
      openZone.sort((a, b) => {
        return a.start - b.start;
      });

      openZone.forEach((item, index) => {
        if (!lockedZone[index]) {
          lockedZone[index] = {};
        }
        if (!lockedZone[index + 1]) {
          lockedZone[index + 1] = {};
        }
        lockedZone[index].end = item.start;
        lockedZone[index + 1].start = item.end;
      });
      lockedZone[0].start = startTimestamp;
      lockedZone[lockedZone.length - 1].end = endTimestamp;
      lockedZone = lockedZone.filter((item) => {
        return item.start < item.end;
      });
    }

    return {
      lockedZone,
      openZone
    };
  });

  watch(chartCoverZone, createMaskLayer);

  const process_activeAttributesTimeChart = () => {
    const result = activeAttributes.value.map((item, index) => {
      const attr = { ...item };
      const attrData = {};

      const maxNum = Math.round(clientWidth * 0.8);
      const lastIndex = chartData.value.length - 1;

      if (!attr.data && attr.valueKey && attr.valueKey !== NULL_TEXT) {
        attr.data = [];
        const supplyItem = [];
        chartData.value.forEach((d, i) => {
          if ([0, lastIndex].includes(i)) {
            supplyItem.push(d);
            return;
          }

          const level = d?.levelMap?.[attr.valueKey] || 0;

          if (unitHelper.isNoValue(level)) {
            return;
          }

          if (!attrData[level]) {
            attrData[level] = [];
          }
          attrData[level].push(d);
        });

        const levels = Reflect.ownKeys(attrData)
          .map((item) => Number(item))
          .sort((a, b) => b - a);

        for (let n = 0, length = levels.length; n < length; n++) {
          const levelData = attrData[levels[n]];
          const nextLevelData = attrData[levels[n + 1]];

          attr.data = attr.data.concat(levelData);

          if (!nextLevelData) {
            break;
          }
          if (attr.data.length >= maxNum) {
            break;
          }
        }

        attr.data = attr.data.concat(supplyItem);

        attr.data.sort((a, b) => {
          return a.timestamp - b.timestamp;
        });
      }

      return attr;
    });

    activeAttributesTimeChart.value = result;
  };

  process_activeAttributesTimeChart();

  // TODO: todo_xjf 跟 distance polyline chart 复用
  const chartNames = computed(() => {
    return activeAttributes.value
      .filter((item) => {
        return !item.noYScale;
      })
      .map((item) => item.chartName);
  });

  // 处理过后的属性参数
  const attributes = computed(() => {
    // console.log(`xjf🔔 activeAttributesTimeChart.value`, activeAttributesTimeChart.value[0].data.length);
    const activeAttributes = unitHelper.clone(activeAttributesTimeChart.value);

    // 功率与风阻的特殊逻辑
    let powerIndex = -1;
    let airPowerIndex = -1;

    activeAttributes.forEach((item, index) => {
      if (item.chartName === 'Power') {
        powerIndex = index;
        return;
      }
      if (item.chartName === 'Air_Power') {
        airPowerIndex = index;
      }
    });

    if (powerIndex >= 0 && airPowerIndex >= 0) {
      // 删除功率，并以功率为基础创建新的属性，绘制河流图
      activeAttributes[powerIndex].chartType = 'drawPowerAndAirPowerChart';
      activeAttributes[airPowerIndex].chartType = 'drawAirPowerChart';
    }

    return activeAttributes;
  });

  // 交给 chart 前最后一次处理
  const processAttributes = (chartAttr) => {
    const subAttrs = [];
    attributes.value.forEach((item) => {
      if (!item.subCharts) {
        return;
      }

      item.subCharts.forEach((chartName) => {
        const chartConfig = chartAttr[chartName];
        if (!chartConfig) {
          return;
        }
        subAttrs.push({
          ...chartConfig,
          chartName
        });
      });
    });
    const attributes2 = attributes.value.concat(
      dealAttrDefine({
        chartAttr,
        attrDefine: [...subAttrs],
        graphObj: graphObj.value
      })
    );
    return attributes2;
  };

  // 绘制遮罩层
  function createMaskLayer() {
    if (!chartRef.value) {
      return;
    }

    const chartCoverLockedZone = chartCoverZone.value.lockedZone || [];
    const chartCoverOpenZone = chartCoverZone.value.openZone || [];

    const startTimestamp = activityDetailStoreRef.activityDetail?.summary.startTimestamp || 0;
    const lockedZone = {
      bg: bgSetting[THEME] || '',
      edgeColor: 'transparent',
      zone: chartCoverLockedZone.map((item, index) => {
        let start = item.start;
        let end = item.end;

        for (let n = 0; n < pauseList.value.length; n++) {
          const pause = pauseList.value[n];
          // 当暂停区间起始大于结束时间时
          if (item.end <= pause.startTimestamp) {
            continue;
          }

          // 开始时间
          if (item.start <= pause.startTimestamp) {
            // 不需要操作
          } else if (pause.startTimestamp < item.start && item.start < pause.endTimestamp) {
            start = start - (item.start - pause.startTimestamp);
          } else {
            start = start - (pause.endTimestamp - pause.startTimestamp);
          }

          // 结束时间
          if (item.end <= pause.startTimestamp) {
            // 不需要操作
          } else if (pause.startTimestamp < item.end && item.end < pause.endTimestamp) {
            end = end - (item.end - pause.startTimestamp);
          } else {
            end = end - (pause.endTimestamp - pause.startTimestamp);
          }
        }
        return {
          start: start - startTimestamp,
          end: end - startTimestamp,
          startText: '',
          endText: ''
        };
      })
    };
    const openZone = {
      bg: 'transparent',
      edgeColor: '#00B3FF',
      zone: chartCoverOpenZone.map((item, index) => {
        let start = item.start;
        let end = item.end;

        for (let n = 0; n < pauseList.value.length; n++) {
          const pause = pauseList.value[n];
          // 当暂停区间起始大于结束时间时
          if (item.end <= pause.startTimestamp) {
            continue;
          }

          // 开始时间
          if (item.start <= pause.startTimestamp) {
            // 不需要操作
          } else if (pause.startTimestamp < item.start && item.start < pause.endTimestamp) {
            start = start - (item.start - pause.startTimestamp);
          } else {
            start = start - (pause.endTimestamp - pause.startTimestamp);
          }

          // 结束时间
          if (item.end <= pause.startTimestamp) {
            // 不需要操作
          } else if (pause.startTimestamp < item.end && item.end < pause.endTimestamp) {
            end = end - (item.end - pause.startTimestamp);
          } else {
            end = end - (pause.endTimestamp - pause.startTimestamp);
          }
        }
        return {
          start: start - startTimestamp,
          end: end - startTimestamp,
          startText: '',
          endText: ''
        };
      })
    };
    chartRef.value.createMaskLayer(lockedZone, openZone);
  }

  if (!watched) {
    // 监听 activeAttributes 变化
    watch(activeAttributes, process_activeAttributesTimeChart);
  }

  return {
    chartNames,
    chartCoverZone,
    createMaskLayer,
    attributes,
    processAttributes
  };
}
